import { z } from "zod";
//#region src/desired-state-envelope.schemas.ts
/**
* The structural schema of the desired-state envelope, and the one parse function that applies it
* (plan 2026-10-20-desired-state-envelope-schema, issue #1009).
*
* Release-engine builds the envelope in `OperatorApiService.computeDesiredState()` and serves it on
* `GET /v1/operator/data-planes/:dpId/desired-state`. The zion-operator program reads it in
* `CpClient.getDesiredState()` and parses every 200 body with {@link parseDesiredStateEnvelope}
* before any reconciler reads it. A body that fails is not served to the reconcile cycle: the
* operator program keeps its last accepted envelope and reports `envelope.schema.invalid`.
*
* Release-ordering rule. The set of checked paths is a release-ordering contract between every CP
* and every operator program in the fleet:
*
* 1. Add a path to the checked set only after every CP in the fleet emits it on every envelope.
*    An operator program that checks a path an older CP omits rejects every envelope of that CP.
* 2. Remove a member from the CP only after every operator program in the fleet tolerates its
*    absence, which means after a release that removed the path from this schema.
* 3. A member outside the checked set is free to come and go. The schema keeps every key it does
*    not know, at every level, so a new CP member never fails an old operator program.
*
* Test `A-170` in `__tests__/contracts.test.ts` pins {@link DESIRED_STATE_ENVELOPE_CHECKED_PATHS}, so
* a change to the set is a visible test edit.
*/
/**
* The eleven envelope paths that {@link DesiredStateEnvelopeSchema} checks, in the order of the
* plan's decision 4. A `*` segment stands for every key of a record or every index of an array.
*
* Each path is one where an absent or null value throws outside every per-app containment of the
* operator program, and where release-engine always emits a value. Every other member, scalars
* included, is not checked.
*
* Who reads it: the contract test `A-170` and the schema spec. It is documentation that a test
* pins; no code derives the schema from it.
*/
const DESIRED_STATE_ENVELOPE_CHECKED_PATHS = [
	"/apps",
	"/services",
	"/apps/*",
	"/apps/*/appManifest",
	"/apps/*/fixtures",
	"/apps/*/fixtures/*",
	"/apps/*/fixtures/*/activeVersions",
	"/apps/*/fixtures/*/defaultVersionsByOrg",
	"/apps/*/fixtures/*/activeVersions/*",
	"/apps/*/fixtures/*/activeVersions/*/channels",
	"/apps/*/fixtures/*/activeVersions/*/fixtureManifest"
];
/**
* A value that is present and not `null`. Any other value passes: an object, an array, a string or
* a number. The operator program validates each manifest blob per app and contains a bad blob to
* that app, so this schema must not turn a per-app fault into a rejection of the whole envelope.
*/
const PresentValueSchema = z.custom((value) => value !== void 0 && value !== null, { error: "Invalid input: expected a value that is present and not null" });
/**
* A record of `value`, with one more refusal than `z.record`: an own key named `__proto__`.
*
* zod 4.4.3 `z.record` skips an own `__proto__` key and does not check its value (measured
* 2026-10-20). `JSON.parse` makes such a key an own property, and `Object.entries` returns it, so
* the operator program would read an entry that the schema never checked. The refusal names the
* record path; the key is in the message.
*/
function checkedRecord(value) {
	return z.custom((input) => typeof input !== "object" || input === null || !Object.hasOwn(input, "__proto__"), { error: "Invalid input: the key \"__proto__\" is not allowed in a record" }).pipe(z.record(z.string(), value));
}
/** One entry of `activeVersions`: paths 9, 10 and 11. */
const ActiveVersionSchema = z.looseObject({
	channels: z.array(z.unknown()),
	fixtureManifest: PresentValueSchema
});
/** One fixture set, `apps[k].fixtures[f]`: paths 6, 7 and 8. */
const FixtureSetSchema = z.looseObject({
	activeVersions: z.array(ActiveVersionSchema),
	defaultVersionsByOrg: z.record(z.string(), z.unknown())
});
/** One app entry, `apps[k]`: paths 3, 4 and 5. */
const AppEntrySchema = z.looseObject({
	appManifest: PresentValueSchema,
	fixtures: checkedRecord(FixtureSetSchema)
});
/**
* The structural schema of the desired-state envelope body that release-engine serves.
*
* Inputs: any value, normally the result of `JSON.parse` on a 200 body. The body may carry
* `etag` or not; the schema does not check it.
*
* Output: `safeParse` succeeds when the eleven paths of {@link DESIRED_STATE_ENVELOPE_CHECKED_PATHS}
* hold:
*
* 1. `apps` and `services` are records: non-null objects that are not arrays.
* 2. Each `apps[k]` is an object with `appManifest` present and not null, and `fixtures` a record.
* 3. Each `fixtures[f]` is an object with `activeVersions` an array (empty allowed) and
*    `defaultVersionsByOrg` a record.
* 4. Each `activeVersions[i]` is an object with `channels` an array and `fixtureManifest` present
*    and not null.
*
* Every other key passes unchecked at every level (`z.looseObject`). The values of `services` and
* of `defaultVersionsByOrg` are not checked. A record that holds an own `__proto__` key fails, for
* the record of `apps` and the record of `fixtures`.
*
* Errors: `safeParse` returns `success: false` with one zod issue per failed check. It never
* throws. A body that is not an object fails at the root path.
*
* Use {@link parseDesiredStateEnvelope}, which returns the input object itself and the issues as
* JSON Pointers. The zod output of this schema is a copy, and `z.record` drops a `__proto__` key
* from it.
*
* The house form `satisfies z.ZodType<DesiredStateEnvelope>` does not apply: the schema checks
* eleven paths and claims nothing about the scalars, so its output type is wider than the
* interface. The type claim lives in {@link parseDesiredStateEnvelope}.
*
* Pure. No side effects.
*/
const DesiredStateEnvelopeSchema = z.looseObject({
	apps: checkedRecord(AppEntrySchema),
	services: z.record(z.string(), z.unknown())
});
/** One RFC 6901 reference token: `~` becomes `~0`, then `/` becomes `~1`. */
function pointerToken(segment) {
	return String(segment).replace(/~/g, "~0").replace(/\//g, "~1");
}
/**
* The failed checks of `body`, as JSON Pointer issues. Empty when `body` passes
* {@link DesiredStateEnvelopeSchema}; zod reports at least one issue for every failed parse.
*/
function failedChecks(body) {
	const result = DesiredStateEnvelopeSchema.safeParse(body);
	return result.success ? [] : result.error.issues.map((issue) => ({
		instancePath: issue.path.map((segment) => `/${pointerToken(segment)}`).join(""),
		message: issue.message
	}));
}
/**
* The narrowing step of {@link parseDesiredStateEnvelope}. `issues` is the result of
* `failedChecks(body)` for this same `body`. The predicate replaces the cast that the operator
* program used before this schema.
*/
function isEnvelopeBody(_body, issues) {
	return issues.length === 0;
}
/**
* Parse one desired-state body with {@link DesiredStateEnvelopeSchema}.
*
* Who calls it: `CpClient.getDesiredState()` in the zion-operator program, on every 200 body, and
* the release-engine producer tests, on the envelope that `computeDesiredState()` builds.
*
* Inputs: any value, normally the result of `JSON.parse`.
*
* Output, per input class:
* - A body that passes: `{ success: true, envelope }`, where `envelope` is `body` itself
*   (`envelope === body`). Every key stays where it is, a `__proto__` key included. A body with
*   an own `__proto__` key in `apps` or in a `fixtures` record fails the parse.
* - A body that fails: `{ success: false, issues }`, one issue per failed check. Each issue is
*   `{ instancePath, message }`. `instancePath` is an RFC 6901 JSON Pointer: `''` for the body
*   itself, `/apps/docs/fixtures` for a member, with `~` written `~0` and `/` written `~1` in a key.
*
* Errors: none thrown. Every failure is a `success: false` result.
*
* Pure. It does not mutate `body` and has no side effects.
*/
function parseDesiredStateEnvelope(body) {
	const issues = failedChecks(body);
	return isEnvelopeBody(body, issues) ? {
		success: true,
		envelope: body
	} : {
		success: false,
		issues
	};
}
//#endregion
export { DESIRED_STATE_ENVELOPE_CHECKED_PATHS, DesiredStateEnvelopeSchema, parseDesiredStateEnvelope };

//# sourceMappingURL=desired-state-envelope.schemas.js.map